import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { h, pct } from '../lib/format';
import { useCategories } from '../lib/categories';
import { StackBars } from './charts';

export interface AreaSummaryView {
  byArea: { key: string; group: 'projekt' | 'intern'; minutes: number }[];
  projectMinutes: number;
  internalMinutes: number;
  projectShare: number;
}

/**
 * Hours by area ("Bereich"): Forma tasks and the categories of own tasks, split into project work
 * (dark) and internal work outside projects (light), e.g. training, R&D, administration.
 */
export function AreaBars({ areas, hint }: { areas: AreaSummaryView; hint?: ReactNode }) {
  const { t } = useTranslation();
  const cats = useCategories();
  const total = areas.projectMinutes + areas.internalMinutes;
  if (!total) return <p className="text-sm text-graphite">{t('common.noData')}</p>;
  const label = (key: string) => (key === 'forma' ? t('areas.forma') : cats.label(key));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
        <span>
          <span className="font-semibold">{t('areas.project')}</span> <span className="num">{pct(areas.projectMinutes / total)}</span>{' '}
          <span className="num text-graphite">({h(areas.projectMinutes, 1)} h)</span>
        </span>
        <span>
          <span className="font-semibold">{t('areas.internal')}</span> <span className="num">{pct(areas.internalMinutes / total)}</span>{' '}
          <span className="num text-graphite">({h(areas.internalMinutes, 1)} h)</span>
        </span>
      </div>
      <StackBars
        head={t('areas.area')}
        labelWidth="15rem"
        series={[
          { key: 'projekt', label: t('areas.project'), kind: 'strong' },
          { key: 'intern', label: t('areas.internal'), kind: 'soft' },
        ]}
        rows={areas.byArea.map((a) => ({
          id: `${a.group}:${a.key}`,
          label: label(a.key),
          values: { [a.group]: a.minutes },
          end: `${h(a.minutes, 1)} · ${pct(a.minutes / total)}`,
        }))}
      />
      {hint && <p className="text-xs text-graphite">{hint}</p>}
    </div>
  );
}
